// src/components/positions/PositionsTable.js
import React, { useState } from 'react';
import { Edit2, Save, X, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const PositionsTable = ({ positions, onUpdatePosition, onDeletePosition }) => {
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  const totalValue = positions.reduce((sum, pos) => sum + (pos.value || 0), 0);

  const handleEdit = (position) => {
    setEditingId(position.id);
    setEditingData({...position});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSave = async (position) => {
    try {
      await onUpdatePosition(position.id, editingData);
      setEditingId(null);
      setEditingData({});
      toast.success('Pozisyon güncellendi');
    } catch (error) {
      toast.error('Pozisyon güncellenemedi');
    }
  };

  const handleDelete = async (position) => {
    if (window.confirm(`${position.symbol} pozisyonunu silmek istediğinize emin misiniz?`)) {
      try {
        await onDeletePosition(position.id);
        toast.success(`${position.symbol} pozisyonu silindi`);
      } catch (error) {
        toast.error('Pozisyon silinemedi');
      }
    }
  };

  const handleChange = (field, value) => {
    setEditingData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (field === 'quantity' || field === 'lastPrice') {
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.lastPrice) || 0;
        const cost = parseFloat(updated.avgCost) || 0;
        
        updated.value = qty * price;
        updated.profitLoss = updated.value - (qty * cost);
      }
      
      if (field === 'avgCost') {
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.lastPrice) || 0;
        const cost = parseFloat(value) || 0;

        updated.value = qty * price;
        updated.profitLoss = updated.value - (qty * cost);
      }

      return updated;
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-700">
        <thead>
          <tr>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-left">Sembol</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Adet</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Son Fiyat</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Değer</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Maliyet</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Kar/Zarar</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">Ağırlık</th>
            <th className="px-4 py-3 bg-gray-800/95 text-gray-200 text-right">İşlem</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {positions.map((position) => {
            const isEditing = editingId === position.id;
            const data = isEditing ? editingData : position;
            const weight = ((data.value || 0) / totalValue) * 100;
            
            return (
              <tr key={position.id} className="bg-gray-900/40 hover:bg-gray-800/50">
                <td className="px-4 py-2">
                  <div className="inline-block rounded bg-gray-800/90 px-2 py-1">
                    {data.symbol}
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  {isEditing ? (
                    <input
                      type="number"
                      value={data.quantity}
                      onChange={(e) => handleChange('quantity', e.target.value)}
                      className="w-24 px-2 py-1 bg-gray-800/70 border border-gray-700 rounded text-right"
                      step="any"
                    />
                  ) : data.quantity}
                </td>
                <td className="px-4 py-2 text-right">
                  {isEditing ? (
                    <input
                      type="number"
                      value={data.lastPrice}
                      onChange={(e) => handleChange('lastPrice', e.target.value)}
                      className="w-24 px-2 py-1 bg-gray-800/70 border border-gray-700 rounded text-right"
                      step="any"
                    />
                  ) : data.lastPrice?.toFixed(2)}
                </td>
                <td className="px-4 py-2 text-right">
                  {data.value?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                </td>
                <td className="px-4 py-2 text-right">
                  {isEditing ? (
                    <input
                      type="number"
                      value={data.avgCost}
                      onChange={(e) => handleChange('avgCost', e.target.value)}
                      className="w-24 px-2 py-1 bg-gray-800/70 border border-gray-700 rounded text-right"
                      step="any"
                    />
                  ) : data.avgCost?.toFixed(2)}
                </td>
                <td className={`px-4 py-2 text-right ${data.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.profitLoss?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                </td>
                <td className="px-4 py-2 text-right">
                  {weight.toFixed(2)}%
                </td>
                <td className="px-4 py-2 text-right">
                  {isEditing ? (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleSave(position)}
                        className="text-green-400 hover:text-green-500"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-red-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(position)}
                        className="text-blue-400 hover:text-blue-500"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(position)}
                        className="text-red-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PositionsTable;